import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Loader2,
  LogOut,
  ShieldCheck,
  Star,
  Download,
} from "lucide-react";
import { useTheme } from "next-themes";

import TeamLogo from "@/components/TeamLogo";
import FavoriteTeamDialog from "@/components/account/FavoriteTeamDialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { getConfiguredSeason } from "@/lib/season";
import { useStandings } from "@/queries/standings";
import "@/styles/native-account-v3.css";
import { useNativeAppUpdate } from "@/contexts/NativeAppUpdateContext";

const NATIVE_SECTIONS = [
  { title: "Appen", items: ["notifications", "appearance"] },
  { title: "Konto", items: ["profile", "security"] },
  { title: "Information", items: ["announcements", "privacy", "legal", "about"] },
];

function themeLabel(theme) {
  if (theme === "dark") return "Mörkt";
  if (theme === "light") return "Ljust";
  return "System";
}

function itemStatus(key, account, resolvedTheme, attention) {
  if (key === "appearance") return themeLabel(resolvedTheme);
  if (key === "matches") return `${account?.stats?.saved || 0} sparade`;
  if (key === "notifications") return attention ? "Åtgärd krävs" : "Hantera";
  if (key === "security") return account?.two_factor_enabled ? "2FA på" : "2FA av";
  return "";
}

function itemLabel(key, fallback) {
  if (key === "matches") return "Mina protokoll";
  if (key === "appearance") return "Utseende";
  if (key === "security") return "Kontosäkerhet";
  return fallback;
}

function AccountLoading() {
  return (
    <div className="native-v3-account-loading" aria-label="Laddar kontouppgifter">
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </div>
  );
}

export default function NativeAccountLayout({
  account,
  activeSection,
  children,
  initials,
  isError,
  isLoading,
  loggingOut,
  navItems,
  needsNotificationsAttention,
  needsProtocolAttention,
  onAccountRoot,
  onLogout,
}) {
  const [favoriteDialogOpen, setFavoriteDialogOpen] = useState(false);
  const { status: appUpdateStatus, update: appUpdate, showUpdate } = useNativeAppUpdate();
  const { resolvedTheme } = useTheme();
  const { data: standings } = useStandings(getConfiguredSeason(), {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const navItemMap = Object.fromEntries(navItems.map((item) => [item.to, item]));
  const favoriteTeam = (standings?.rows || []).find(
    (row) => String(row.team_id || "") === String(account?.favorite_team_id || "")
  );
  const favoriteRank = favoriteTeam?.position || favoriteTeam?.rank || null;

  if (!onAccountRoot) {
    return (
      <div className="native-v3-account native-v3-account-detail">
        <header className="native-v3-account-detail-header">
          <Link to="/account" aria-label="Tillbaka till Mina sidor">
            <ArrowLeft aria-hidden="true" />
            <span>Tillbaka</span>
          </Link>
          <h1>{activeSection?.label || "Mina sidor"}</h1>
          <span aria-hidden="true" />
        </header>
        <main
          className={activeSection?.to === "legal" ? "native-v3-account-legal-subpage" : "native-v3-account-subpage"}
          data-section={activeSection?.to || "account"}
        >
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="native-v3-account native-v3-account-root">
      <header className="native-v3-account-titlebar">
        <h1>Mina sidor</h1>
      </header>

      {isLoading ? (
        <AccountLoading />
      ) : (
        <>
          <section className="native-v3-account-identity">
            <Avatar className="native-v3-account-profile-avatar">
              <AvatarImage src={account?.avatar_url || ""} alt={account?.display_name || "Profilbild"} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="native-v3-account-identity-copy">
              <h2>{account?.display_name || account?.email || "Lapstr-medlem"}</h2>
              <p>Lapstr-medlem</p>
              <span>
                {account?.email_verified ? <CheckCircle2 aria-hidden="true" /> : null}
                {account?.email || "Ingen e-postadress"}
              </span>
            </div>
          </section>

          <button
            className="native-v3-account-favorite"
            type="button"
            onClick={() => setFavoriteDialogOpen(true)}
          >
            <div className="native-v3-account-section-label">Mitt favoritlag</div>
            <div className="native-v3-account-favorite-row">
              {favoriteTeam ? (
                <TeamLogo teamName={favoriteTeam.team} size={48} className="native-v3-account-team-logo" />
              ) : (
                <span className="native-v3-account-team-placeholder"><Star aria-hidden="true" /></span>
              )}
              <span className="native-v3-account-favorite-copy">
                <strong>{favoriteTeam?.team || "Välj favoritlag"}</strong>
                <small>{favoriteTeam?.city || "Anpassa din startsida"}</small>
              </span>
              {favoriteRank ? <b>#{favoriteRank}</b> : null}
              <em>{favoriteTeam ? "Byt favoritlag" : "Välj lag"}</em>
              <ChevronRight aria-hidden="true" />
            </div>
          </button>
          <FavoriteTeamDialog open={favoriteDialogOpen} onOpenChange={setFavoriteDialogOpen} />

          <section className="native-v3-account-stats" aria-label="Kontostatistik">
            <div><strong>{account?.stats?.protocols || 0}</strong><span>protokoll</span></div>
            <div><strong>{account?.stats?.saved || 0}</strong><span>sparade</span></div>
            <div><strong>{account?.two_factor_enabled ? "På" : "Av"}</strong><span>2FA</span></div>
          </section>

          <Link className="native-v3-account-protocols" to="/account/matches">
            <div className="native-v3-account-section-label">Mina protokoll</div>
            <div className="native-v3-account-protocols-row">
              <span className="native-v3-account-protocols-icon"><ClipboardList aria-hidden="true" /></span>
              <span>
                <strong>Hantera matchprotokoll</strong>
              </span>
              {needsProtocolAttention ? <i aria-label="Kräver uppmärksamhet" /> : null}
              <ChevronRight aria-hidden="true" />
            </div>
          </Link>

          {appUpdateStatus === "optional" ? (
            <button className="native-v3-account-app-update" type="button" onClick={showUpdate}>
              <span><Download aria-hidden="true" /></span>
              <strong>Ny version av Lapstr</strong>
              <small>{appUpdate?.downloaded ? "Redo att installeras" : "Uppdatering tillgänglig"}</small>
              <ChevronRight aria-hidden="true" />
            </button>
          ) : null}
        </>
      )}

      {NATIVE_SECTIONS.map(({ title, items }) => (
        <section className="native-v3-account-menu" key={title}>
          <h2>{title}</h2>
          <div>
            {items.map((key) => {
              const item = navItemMap[key];
              if (!item) return null;
              const Icon = item.icon;
              const attention = key === "notifications"
                ? needsNotificationsAttention
                : key === "matches" && needsProtocolAttention;
              return (
                <Link key={key} to={item.to} className="native-v3-account-menu-row">
                  <Icon aria-hidden="true" />
                  <span>{itemLabel(key, item.label)}</span>
                  {attention ? <i aria-label="Kräver uppmärksamhet" /> : null}
                  <small data-attention={attention ? "true" : "false"}>
                    {itemStatus(key, account, resolvedTheme, attention)}
                  </small>
                  <ChevronRight aria-hidden="true" />
                </Link>
              );
            })}
          </div>
        </section>
      ))}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button className="native-v3-account-logout" type="button" disabled={loggingOut}>
            {loggingOut ? <Loader2 className="animate-spin" aria-hidden="true" /> : <LogOut aria-hidden="true" />}
            <span>{loggingOut ? "Loggar ut..." : "Logga ut"}</span>
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Logga ut?</AlertDialogTitle>
            <AlertDialogDescription>
              Du loggas ut från den här enheten och skickas tillbaka till inloggningen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={onLogout}>Logga ut</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isError ? <p className="native-v3-account-error">Kunde inte hämta kontouppgifter just nu.</p> : null}
      <span className="native-v3-account-security-note"><ShieldCheck aria-hidden="true" /> Dina kontouppgifter hanteras säkert.</span>
    </div>
  );
}
